import React, { useEffect, useState } from 'react'
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore'
import { db } from '../../firebase/firebase'
import { t } from '../../components/i18n/i18n'
import back from '../../assets/images/elements/timeHeader.jpg'

const DAY_ORDER = { mon: 0, tue: 1, wed: 2, thu: 3, fri: 4, sat: 5, sun: 6 }

// ── Hardcoded fallback (shown while Firestore loads or if collection is empty) ──
const FALLBACK_DAYS = [
  {
    key: 'tue', labelUa: 'Вівторок', labelEn: 'Tuesday', accent: '#f07a28', icon: '🙏',
    events: [
      { time: '10:00', descUa: 'Молитовне зібрання для сестер',                              descEn: 'Prayer gathering for sisters' },
      { time: '17:00', descUa: 'Мами в молитві',                                              descEn: 'Mothers in prayer' },
      { time: '19:00', descUa: 'Вивчення Біблії для братів; Окремо - спілкування сестер',    descEn: 'Bible study for brothers; Separately - sisters communication' },
    ],
  },
  {
    key: 'wed', labelUa: 'Середа', labelEn: 'Wednesday', accent: '#d4a800', icon: '📖',
    events: [
      { time: '19:00', descUa: 'Вивчення Святого Письма для молоді', descEn: 'Bible study for youth' },
    ],
  },
  {
    key: 'fri', labelUa: 'П\'ятниця', labelEn: 'Friday', accent: '#3aaa6e', icon: '✝',
    events: [
      { time: '10:00', descUa: 'Загальноцерковне Молитовне служіння', descEn: 'General church prayer worship' },
    ],
  },
  {
    key: 'sat', labelUa: 'Субота', labelEn: 'Saturday', accent: '#3a8fd9', icon: '👥',
    events: [
      { time: '12:00', descUa: 'Аванна',               descEn: 'Avanna' },
      { time: '15:00', descUa: 'Підліткове служіння',   descEn: 'Youth worshipping' },
      { time: '18:00', descUa: 'Спілкування молоді',    descEn: 'Youth communication' },
    ],
  },
  {
    key: 'sun', labelUa: 'Неділя', labelEn: 'Sunday', accent: '#7c5cbf', icon: '⛪',
    events: [
      { time: '10:00', descUa: 'Загальноцерковне Ранкове зібрання', descEn: 'General Morning Worship' },
      { time: '18:00', descUa: 'Загальноцерковне Вечірнє зібрання', descEn: 'General Evening Worship' },
    ],
  },
]

const MOBILE_BP = 600

// ── Day card ──────────────────────────────────────────────────────────────────

const DayCard = ({ day, full = false, mobile = false, lang = 'ua' }) => (
  <div style={{ ...s.card, ...(full ? s.cardFull : {}) }}>
    <div style={{ ...s.cardHead, background: day.accent }}>
      <span style={s.cardIcon}>{day.icon}</span>
      <span style={s.cardDay}>{lang === 'en' && day.labelEn ? day.labelEn : day.labelUa}</span>
    </div>
    <div style={{ ...s.cardBody, ...(full ? s.cardBodyFull : {}) }}>
      {day.events.map((ev, i) => (
        <div key={i} style={mobile ? s.eventMobile : s.event}>
          <span style={{ ...s.timePill, background: day.accent }}>{ev.time}</span>
          <span style={s.eventDesc}>{lang === 'en' && ev.descEn ? ev.descEn : ev.descUa}</span>
        </div>
      ))}
    </div>
  </div>
)

// ── Main ──────────────────────────────────────────────────────────────────────

export const Schedule = () => {
  const lang = localStorage.getItem('userLanguage') === 'en' ? 'en' : 'ua'
  const [mobile, setMobile] = useState(window.innerWidth < MOBILE_BP)
  const [days, setDays] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < MOBILE_BP)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    let rawDays = []
    let rawEvents = []
    let daysLoaded = false
    let eventsLoaded = false

    const merge = () => {
      if (!daysLoaded || !eventsLoaded) return
      const merged = rawDays
        .map(day => ({
          ...day,
          events: rawEvents
            .filter(e => e.dayKey === day.key)
            .sort((a, b) => a.order - b.order),
        }))
        .sort((a, b) => (DAY_ORDER[a.key] ?? 99) - (DAY_ORDER[b.key] ?? 99))
      setDays(merged.length > 0 ? merged : [])
      setLoading(false)
    }

    const unsubDays = onSnapshot(
      query(collection(db, 'scheduleDays'), orderBy('order')),
      snap => {
        rawDays = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        daysLoaded = true
        merge()
      }
    )
    const unsubEvents = onSnapshot(
      query(collection(db, 'scheduleEvents'), orderBy('order')),
      snap => {
        rawEvents = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        eventsLoaded = true
        merge()
      }
    )
    return () => { unsubDays(); unsubEvents() }
  }, [])

  // Use Firestore data if loaded and non-empty, otherwise fallback
  const displayDays = (!loading && days.length > 0) ? days : FALLBACK_DAYS

  // Last day displayed full-width (Sunday-style)
  const gridDays = displayDays.slice(0, -1)
  const lastDay  = displayDays[displayDays.length - 1]

  return (
    <>
      <div style={s.hero}>
        <img src={back} alt='' style={s.heroImg} />
        <div style={s.heroOverlay} />
        <h1 style={s.heroTitle}>{t('scheduleHeader')}</h1>
      </div>

      <div style={s.page}>
        <div style={{ ...s.grid, gridTemplateColumns: mobile ? '1fr' : 'repeat(2, 1fr)' }}>
          {gridDays.map(day => <DayCard key={day.key} day={day} mobile={mobile} lang={lang} />)}
        </div>
        {lastDay && <DayCard day={lastDay} full mobile={mobile} lang={lang} />}
      </div>
    </>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  hero: { position: 'relative', width: '100%', height: '15vh', overflow: 'hidden' },
  heroImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  heroOverlay: {
    position: 'absolute', inset: 0,
    background: 'linear-gradient(to bottom, rgba(18,8,3,0.45) 0%, rgba(45,31,20,0.7) 100%)',
  },
  heroTitle: {
    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: 0, fontSize: '36px', fontWeight: '800', color: '#fff',
    textShadow: '0 2px 16px rgba(0,0,0,0.6)', letterSpacing: '0.5px',
    fontFamily: '"Segoe UI", system-ui, sans-serif', textAlign: 'center', padding: '0 24px',
  },

  page: {
    maxWidth: '900px', margin: '0 auto 60px', padding: '28px 16px 0',
    display: 'flex', flexDirection: 'column', gap: '16px',
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' },

  card: {
    background: '#fff', borderRadius: '16px', overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(92,61,46,0.12)', border: '1px solid #f0e4d4',
  },
  cardFull: {},
  cardHead: { display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 20px' },
  cardIcon: { fontSize: '20px', lineHeight: 1 },
  cardDay: {
    fontSize: '18px', fontWeight: '800', color: '#fff', letterSpacing: '0.3px',
    textShadow: '0 1px 4px rgba(0,0,0,0.25)', fontFamily: '"Segoe UI", system-ui, sans-serif',
    textTransform: 'uppercase',
  },
  cardBody: { padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' },
  cardBodyFull: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px 40px' },

  event: { display: 'flex', alignItems: 'flex-start', gap: '12px' },
  eventMobile: { display: 'flex', flexDirection: 'column', gap: '4px' },
  timePill: {
    flexShrink: 0, color: '#fff', fontSize: '13px', fontWeight: '700',
    padding: '4px 12px', borderRadius: '20px', letterSpacing: '0.5px',
    fontFamily: '"Segoe UI", system-ui, sans-serif', marginTop: '1px',
  },
  eventDesc: { fontSize: '15px', color: '#2d1f14', lineHeight: '1.5', fontFamily: '"Segoe UI", system-ui, sans-serif' },
}
